<script lang="ts">
  import { _, waitLocale, locale } from "svelte-i18n";
  import { SvelteSet } from "svelte/reactivity";
  import { goto } from "$app/navigation";
  import { page } from "$app/state";
  import { browser } from "$app/environment";
  import { currentUser, isImpersonating } from "$lib/stores/auth";
  import { modulesAccess } from "$lib/stores/modulesAccess";
  import { dashboardAccess } from "$lib/stores/dashboardAccess";
  import { productImportAccess } from "$lib/stores/productImportAccess";
  import { productCmsAccess } from "$lib/stores/productCmsAccess";
  import { pricingAccess } from "$lib/stores/pricingAccess";
  import { productCmsAiAccess } from "$lib/stores/productCmsAiAccess";
  import { seoCmsAccess } from "$lib/stores/seoCmsAccess";
  import { wmsAccess } from "$lib/stores/wmsAccess";
  import { getWmsShortcuts } from "$lib/wms/app/wms-navigation";
  import { WMS_APP_START_PATH } from "$lib/wms/app/wms-app";
  import { isSuperAdminEnabled } from "$lib/config/build";
  import { hasAnyRole, hasRole } from "$lib/utils/userRoleAccess";
  import * as DropdownMenu from "$lib/components/ui/dropdown-menu/index.js";
  import * as Sidebar from "$lib/components/ui/sidebar/index.js";
  import { Button } from "$lib/components/ui/button/index.js";
  import { Badge } from "$lib/components/ui/badge/index.js";
  import BarChart3 from '@lucide/svelte/icons/bar-chart-3';
  import Bolt from '@lucide/svelte/icons/bolt';
  import Boxes from '@lucide/svelte/icons/boxes';
  import ChevronRight from '@lucide/svelte/icons/chevron-right';
  import House from '@lucide/svelte/icons/house';
  import LogOut from '@lucide/svelte/icons/log-out';
  import Moon from '@lucide/svelte/icons/moon';
  import Package from '@lucide/svelte/icons/package';
  import Shield from '@lucide/svelte/icons/shield';
  import Sun from '@lucide/svelte/icons/sun';
  import Tags from '@lucide/svelte/icons/tags';
  import { useSidebar } from "$lib/components/ui/sidebar/context.svelte.js";
  import { theme, toggleTheme } from "$lib/stores/theme";
  import { mergeProps } from "bits-ui";

  const { onLogout = () => {} } = $props();

  let i18nReady = $state(false);
  const expandedMenus = new SvelteSet<string>();
  const sidebar = useSidebar();

  const languages = [
    { code: 'no', label: 'NO' },
    { code: 'sv', label: 'SV' },
    { code: 'da', label: 'DA' },
    { code: 'en', label: 'EN' },
  ] as const;

  function changeLanguage(lang: string) {
    if (browser) {
      localStorage.setItem('language', lang);
      locale.set(lang);
    }
  }

  waitLocale().then(() => {
    i18nReady = true;
  });

  const currentPath = $derived(page.url.pathname);
  const isAdmin = $derived(hasAnyRole($currentUser, ["Admin", "SuperAdmin"]));
  const isSuperAdmin = $derived(hasRole($currentUser, "SuperAdmin"));
  const canAccessPosters = $derived(Boolean($modulesAccess.posters?.canAccessModule));
  const canAccessProductLocations = $derived(Boolean($modulesAccess.productLocations?.canAccessModule));
  const canAccessDashboard = $derived($dashboardAccess.canAccessModule);
  const canViewDashboardWorkspace = $derived($dashboardAccess.canReadDashboard);
  const canViewDashboardExplore = $derived($dashboardAccess.canReadDashboard);
  const canViewDashboardNavigation = $derived(canAccessDashboard && (canViewDashboardWorkspace || canViewDashboardExplore));
  const canAccessProductCms = $derived($productCmsAccess.canAccessModule);
  const canAccessProductCmsAi = $derived($productCmsAiAccess.canAccessModule);
  const canViewProductCmsSearch = $derived($productCmsAccess.canReadProducts || $productCmsAccess.canWriteProducts);
  const canViewProductCmsSettings = $derived(canAccessProductCms);
  const canViewProductCmsImport = $derived($productImportAccess.canRunBulkImport);
  const canViewProductCmsAiSettings = $derived($productCmsAiAccess.canViewWithinProductCmsSettings);
  const wmsShortcuts = $derived(getWmsShortcuts($wmsAccess, (key) => $_(key)));
  const canViewWmsNavigation = $derived(wmsShortcuts.length > 0);
  const canViewProductCmsNavigation = $derived(canViewProductCmsSearch || canViewProductCmsSettings || canViewProductCmsAiSettings || canAccessProductCmsAi);
  const canAccessSeoCms = $derived($seoCmsAccess.canAccessModule);
  const canViewSeoCmsContent = $derived($seoCmsAccess.canReadContent || $seoCmsAccess.canWriteContent);
  const canViewSeoCmsSettings = $derived($seoCmsAccess.canWriteContent);
  const canViewSeoCmsNavigation = $derived(canAccessSeoCms);

  const iconMap = {
    home: House,
    dashboard: BarChart3,
    products: Package,
    productCms: Package,
    pricing: Tags,
    seoCms: Package,
    vms: Boxes,
    admin: Shield,
    superadmin: Bolt,
  };

  type NavIcon = keyof typeof iconMap;

  type NavChild = {
    name: string;
    href: string;
    active: boolean;
    visible: boolean;
  };

  type NavItem = {
    id?: string;
    name: string;
    icon: NavIcon;
    visible: boolean;
    active: boolean;
    href?: string;
    children?: NavChild[];
  };

  const navItems = $derived<NavItem[]>([
    {
      name: $_("menu.home"),
      href: "/",
      icon: "home",
      visible: true,
      active: currentPath === "/",
    },
    {
      id: "dashboard",
      name: $_("menu.dashboard", { default: "Dashboard" }),
      icon: "dashboard",
      visible: canViewDashboardNavigation,
      active: currentPath.startsWith("/dashboard"),
      children: [
        { name: $_('moduleSetup.overview.title'), href: '/dashboard/innstillinger', active: currentPath.startsWith('/dashboard/innstillinger'), visible: canViewDashboardWorkspace },
        {
          name: "Arbeidsflate",
          href: "/dashboard",
          active: currentPath === "/dashboard",
          visible: canViewDashboardWorkspace,
        },
        {
          name: "Utforsk",
          href: "/dashboard/explore",
          active: currentPath.startsWith("/dashboard/explore"),
          visible: canViewDashboardExplore,
        },
        {
          name: $_('dashboard.screens.title'),
          href: '/dashboard/screens',
          active: currentPath.startsWith('/dashboard/screens'),
          visible: canViewDashboardExplore,
        },
      ],
    },
    {
      id: "products",
      name: $_("menu.products"),
      icon: "products",
      visible: canAccessPosters || canAccessProductLocations || canViewProductCmsImport,
      active: currentPath.startsWith("/plakat") || currentPath.startsWith("/produkt/lokasjon") || currentPath.startsWith("/produkt-cms/import"),
      children: [
        { name: $_('moduleSetup.overview.import'), href: '/produkt-cms/import/settings', active: currentPath === '/produkt-cms/import/settings', visible: canViewProductCmsImport },
        { name: $_('moduleSetup.overview.posters'), href: '/plakat/innstillinger', active: currentPath === '/plakat/innstillinger', visible: canAccessPosters },
        { name: $_('moduleSetup.overview.locations'), href: '/produkt/lokasjon/innstillinger', active: currentPath === '/produkt/lokasjon/innstillinger', visible: canAccessProductLocations },
        {
          name: $_("menu.productCmsImport"),
          href: "/produkt-cms/import",
          active: currentPath.startsWith("/produkt-cms/import"),
          visible: canViewProductCmsImport,
        },
        {
          name: $_("menu.productPosters"),
          href: "/plakat",
          active: currentPath === "/plakat",
          visible: canAccessPosters,
        },
        {
          name: $_("menu.productLocations"),
          href: "/produkt/lokasjon",
          active: currentPath.startsWith("/produkt/lokasjon"),
          visible: canAccessProductLocations,
        },
      ],
    },
    {
      id: "product-cms",
      name: $_("menu.productCms", { default: "Produkt CMS" }),
      icon: "productCms",
      visible: canViewProductCmsNavigation,
      active: currentPath.startsWith("/produkt-cms") && !currentPath.startsWith("/produkt-cms/import"),
      children: [
        {
          name: $_('productCms.creation.newProduct'),
          href: '/produkt-cms/new',
          active: currentPath.startsWith('/produkt-cms/new'),
          visible: $productCmsAccess.canWriteProducts,
        },
        {
          name: "Søk",
          href: "/produkt-cms",
          active: currentPath === "/produkt-cms",
          visible: canViewProductCmsSearch,
        },
        {
          name: "Editor",
          href: "/produkt-cms/editor",
          active: currentPath.startsWith("/produkt-cms/editor"),
          visible: canViewProductCmsSearch,
        },
        {
          name: $_("menu.productCmsGroupTrees"),
          href: "/produkt-cms/group-trees",
          active: currentPath.startsWith("/produkt-cms/group-trees"),
          visible: canViewProductCmsSearch,
        },
        {
          name: $_("menu.settings", { default: "Innstillinger" }),
          href: "/produkt-cms/settings",
          active: currentPath.startsWith("/produkt-cms/settings"),
          visible: canViewProductCmsSettings,
        },
        {
          name: "Diagnostikk",
          href: "/produkt-cms/diagnostics",
          active: currentPath.startsWith("/produkt-cms/diagnostics"),
          visible: canViewProductCmsSettings,
        },
        {
          name: $_("menu.aiSettings", { default: "AI-innstillinger" }),
          href: "/produkt-cms/ai/settings",
          active: currentPath.startsWith("/produkt-cms/ai/settings"),
          visible: canViewProductCmsAiSettings,
        },
      ],
    },
    {
      id: "pricing",
      name: $_("pricing.title"),
      href: "/priser",
      icon: "pricing",
      visible: $pricingAccess.canAccessModule && $pricingAccess.canReadPricing,
      active: currentPath === "/priser" || currentPath.startsWith("/priser/"),
    },
    {
      id: "seo-cms",
      name: $_("menu.seoCms", { default: "SEO CMS" }),
      icon: "seoCms",
      visible: canViewSeoCmsNavigation,
      active: currentPath.startsWith("/seo-cms"),
      children: [
        {
          name: $_("seoCms.menu.overview", { default: "Oversikt" }),
          href: "/seo-cms",
          active: currentPath === "/seo-cms",
          visible: canAccessSeoCms,
        },
        {
          name: $_("seoCms.menu.explorer", { default: "Innholdsutforsker" }),
          href: "/seo-cms/debug",
          active: currentPath.startsWith("/seo-cms/debug"),
          visible: canViewSeoCmsContent,
        },
        {
          name: $_("seoCms.menu.settings", { default: "Innstillinger" }),
          href: "/seo-cms/innstillinger",
          active: currentPath.startsWith("/seo-cms/innstillinger"),
          visible: canViewSeoCmsSettings,
        },
      ],
    },
    {
      id: "vms",
      name: $_("menu.vms", { default: "WMS" }),
      icon: "vms",
      visible: canViewWmsNavigation,
      active: currentPath.startsWith("/wms"),
      children: [
        {
          name: $_("vms.app.home"),
          href: WMS_APP_START_PATH,
          active: currentPath === WMS_APP_START_PATH,
          visible: true,
        },
        ...wmsShortcuts.map((shortcut) => ({
          name: shortcut.title,
          href: shortcut.href,
          active: currentPath === shortcut.href || currentPath.startsWith(shortcut.href + "/"),
          visible: true,
        })),
      ],
    },
    {
      id: "admin",
      name: $_("menu.admin"),
      icon: "admin",
      visible: isAdmin,
      active: currentPath.startsWith("/admin"),
      children: [
        {
          name: $_("menu.users"),
          href: "/admin/users",
          active: currentPath.startsWith("/admin/users"),
          visible: isAdmin,
        },
        {
          name: $_("menu.integrations", { default: "Integrasjoner" }),
          href: "/admin/integrations",
          active: currentPath.startsWith("/admin/integrations"),
          visible: isAdmin,
        },
        {
          name: $_("menu.settings"),
          href: "/admin/settings",
          active: currentPath.startsWith("/admin/settings"),
          visible: isAdmin,
        },
      ],
    },
    {
      id: "superadmin",
      name: $_("menu.superadmin"),
      icon: "superadmin",
      visible: isSuperAdmin && isSuperAdminEnabled(),
      active: currentPath.startsWith("/superadmin"),
      children: [
        {
          name: $_("menu.dashboard"),
          href: "/superadmin",
          active: currentPath === "/superadmin",
          visible: true,
        },
        {
          name: $_("menu.home"),
          href: "/superadmin/home",
          active: currentPath === "/superadmin/home",
          visible: true,
        },
        {
          name: $_("menu.companies"),
          href: "/superadmin/firmaer",
          active: currentPath.startsWith("/superadmin/firmaer"),
          visible: true,
        },
        {
          name: $_("menu.templates"),
          href: "/superadmin/maler",
          active: currentPath.startsWith("/superadmin/maler"),
          visible: true,
        },
        {
          name: $_("menu.services"),
          href: "/superadmin/services",
          active: currentPath.startsWith("/superadmin/services"),
          visible: true,
        },
        {
          name: $_("superadmin.agentKeys.title"),
          href: "/superadmin/api-keys",
          active: currentPath.startsWith("/superadmin/api-keys"),
          visible: true,
        },
        {
          name: $_("menu.logs"),
          href: "/superadmin/logs",
          active: currentPath.startsWith("/superadmin/logs"),
          visible: true,
        },
        {
          name: $_("menu.sql"),
          href: "/superadmin/sql",
          active: currentPath.startsWith("/superadmin/sql"),
          visible: true,
        },
      ],
    },
  ]);

  const activeParentIds = $derived(
    navItems
      .filter((item) => item.children && item.active)
      .map((item) => item.id ?? item.name)
  );

  $effect(() => {
    if (activeParentIds.length === 0) return;

    for (const id of activeParentIds) {
      if (!expandedMenus.has(id)) {
        expandedMenus.add(id);
      }
    }
  });

  function toggleSubmenu(menuId: string): void {
    if (expandedMenus.has(menuId)) {
      expandedMenus.delete(menuId);
    } else {
      expandedMenus.add(menuId);
    }
  }

  function closeMobileSidebar(): void {
    if (sidebar.isMobile) {
      sidebar.setOpenMobile(false);
    }
  }

  async function handleLogout() {
    if (browser && !window.confirm($_('menu.logoutConfirm', { default: 'Vil du logge ut nå?' }))) {
      return;
    }

    closeMobileSidebar();
    await Promise.resolve(onLogout());
  }
</script>

{#if i18nReady}
  <Sidebar.Root collapsible="icon" class="border-r border-sidebar-border">
    <Sidebar.Header class="border-b border-sidebar-border">
      <div class="px-2 py-1">
        <p class="truncate text-sm font-semibold text-sidebar-foreground">
          {$currentUser?.companyName || $_("common.brandFallback")}
        </p>
      </div>
    </Sidebar.Header>

    <Sidebar.Content>
      <Sidebar.Group>
        <Sidebar.Menu>
          {#each navItems as item (item.id ?? item.name)}
            {#if item.visible}
              {@const ItemIcon = iconMap[item.icon]}
              {@const itemId = item.id ?? item.name}
              {@const childItems = item.children ?? []}
              <Sidebar.MenuItem>
                {#if childItems.length > 0}
                  {#if sidebar.state === "collapsed" && !sidebar.isMobile}
                    <Sidebar.MenuButton isActive={item.active}>
                      {#snippet child({ props })}
                        <DropdownMenu.Root>
                          <DropdownMenu.Trigger>
                            {#snippet child({ props: triggerProps })}
                              <button {...mergeProps(props, triggerProps)} type="button">
                                <ItemIcon />
                                <span>{item.name}</span>
                                <ChevronRight class="ml-auto hidden" />
                              </button>
                            {/snippet}
                          </DropdownMenu.Trigger>
                          <DropdownMenu.Content class="w-56" align="start" side="right" sideOffset={8}>
                            {#each childItems as childItem (childItem.href)}
                              {#if childItem.visible}
                                <DropdownMenu.Item
                                  class="cursor-pointer!"
                                  onSelect={() => {
                                    closeMobileSidebar();
                                    void goto(childItem.href);
                                  }}
                                >
                                  {childItem.name}
                                </DropdownMenu.Item>
                              {/if}
                            {/each}
                          </DropdownMenu.Content>
                        </DropdownMenu.Root>
                      {/snippet}
                    </Sidebar.MenuButton>
                  {:else}
                    <Sidebar.MenuButton
                      isActive={item.active}
                      tooltipContent={item.name}
                      onclick={() => toggleSubmenu(itemId)}
                    >
                      <ItemIcon />
                      <span>{item.name}</span>
                      <ChevronRight
                        class={`ml-auto transition-transform ${expandedMenus.has(itemId) ? "rotate-90" : ""}`}
                      />
                    </Sidebar.MenuButton>

                    {#if expandedMenus.has(itemId)}
                      <Sidebar.MenuSub>
                        {#each childItems as child (child.href)}
                          {#if child.visible}
                            <Sidebar.MenuSubItem>
                              <Sidebar.MenuSubButton href={child.href} isActive={child.active} onclick={closeMobileSidebar}>
                                <span>{child.name}</span>
                              </Sidebar.MenuSubButton>
                            </Sidebar.MenuSubItem>
                          {/if}
                        {/each}
                      </Sidebar.MenuSub>
                    {/if}
                  {/if}
                {:else if item.href}
                  <Sidebar.MenuButton isActive={item.active} tooltipContent={item.name}>
                    {#snippet child({ props })}
                      <a {...props} href={item.href} onclick={closeMobileSidebar}>
                        <ItemIcon />
                        <span>{item.name}</span>
                      </a>
                    {/snippet}
                  </Sidebar.MenuButton>
                {/if}
              </Sidebar.MenuItem>
            {/if}
          {/each}
        </Sidebar.Menu>
      </Sidebar.Group>
    </Sidebar.Content>

    <Sidebar.Footer class="border-t border-sidebar-border">
      {#if $currentUser}
        <div class="space-y-1 px-2 group-data-[collapsible=icon]:hidden">
          <p class="text-xs text-muted-foreground">{$_("profile.loggedInAs")}</p>
          <p class="truncate text-sm font-medium">{$currentUser.email}</p>
          {#if $currentUser.companyName}
            <p class="truncate text-xs text-muted-foreground">{$currentUser.companyName}</p>
          {/if}
          {#if $isImpersonating}
            <Badge class="mt-1 bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
              {$_("profile.impersonating")}
            </Badge>
          {/if}
        </div>

        <div class="flex items-center gap-1 px-1 group-data-[collapsible=icon]:hidden">
          {#each languages as lang (lang.code)}
            <button
              onclick={() => changeLanguage(lang.code)}
              class="rounded px-1.5 py-0.5 text-xs font-medium transition-colors"
              class:bg-primary={$locale === lang.code}
              class:text-primary-foreground={$locale === lang.code}
              class:text-muted-foreground={$locale !== lang.code}
              class:hover:bg-muted={$locale !== lang.code}
            >
              {lang.label}
            </button>
          {/each}
          <button
            onclick={toggleTheme}
            class="ml-auto rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="Toggle theme"
          >
            {#if $theme === 'dark'}
              <Sun class="h-4 w-4" />
            {:else}
              <Moon class="h-4 w-4" />
            {/if}
          </button>
        </div>

        <Button
          variant={sidebar.state === "collapsed" && !sidebar.isMobile ? "outline" : "destructive"}
          class={sidebar.state === "collapsed" && !sidebar.isMobile ? "w-full justify-center px-0" : "w-full justify-start"}
          onclick={handleLogout}
          title={$_('menu.logout')}
          aria-label={$_('menu.logout')}
        >
          <LogOut class="h-4 w-4" />
          <span class="group-data-[collapsible=icon]:hidden">{$_('menu.logout')}</span>
        </Button>
      {/if}
    </Sidebar.Footer>

    <Sidebar.Rail />
  </Sidebar.Root>
{:else}
  <div class="hidden md:flex w-(--sidebar-width) items-center justify-center border-r border-sidebar-border bg-sidebar">
    <div class="h-8 w-8 animate-spin rounded-full border-2 border-sidebar-foreground/30 border-t-sidebar-foreground"></div>
  </div>
{/if}

